import { ENHANCED_VISUAL_CONTENT_PROMPT, shouldUseCardFormat, getVisualFormatHint } from '../../knowledge/visualContentPrompts_v2';
import { validateAndFixTables, containsTables, preProcessTables } from '../../utils/tableValidator';

export const BASE_WHITEBOARD_INSTRUCTION = `
WHITEBOARD USAGE INSTRUCTIONS:
You have access to a "Whiteboard" for visual content.
Use it for:
- Markdown tables (always use the whiteboard for tables)
- Mermaid diagrams (flowcharts, sequence diagrams, mindmaps)
- Code blocks (if they are long or explanatory)
- Step-by-step problem solving
- Formulas and equations
- Any numerical work

CHART FORMAT (for trends, distributions, comparisons):
\`\`\`json:chart
{
  "type": "bar|line|pie",
  "data": [{"name": "A", "value": 10}, ...]
}
\`\`\`

Keep your chat responses conversational and explanatory, while moving detailed computations to the whiteboard.
This helps students focus on understanding the process without being overwhelmed by numbers.
`;

export const WHITEBOARD_SYSTEM_INSTRUCTION = BASE_WHITEBOARD_INSTRUCTION + '\n\n' + ENHANCED_VISUAL_CONTENT_PROMPT;

/**
 * Extract referenced attachments from conversation history
 */
export const extractReferencedAttachments = (
    response: string,
    conversationHistory: any[]
): string[] => {
    const referenced: string[] = [];
    // Simple heuristic: check if response mentions attachment names
    // In a real implementation, we might use a more robust method or ask the AI to cite them

    // Look for recent attachments in history
    const recentAttachments = conversationHistory
        .filter(msg => msg.attachments && msg.attachments.length > 0)
        .flatMap(msg => msg.attachments)
        .slice(-5); // Check last 5 attachments

    for (const att of recentAttachments) {
        if (att.name && response.includes(att.name)) {
            referenced.push(att.name);
        }
    }

    return [...new Set(referenced)];
};

/**
 * Check if user is asking for visual analysis/charts
 */
export const isRequestingVisualAnalysis = (prompt: string): boolean => {
    const visualKeywords = [
        'chart', 'graph', 'plot', 'visualize', 'diagram', 'flowchart',
        'table', 'compare', 'trend', 'distribution', 'breakdown',
        'mindmap', 'structure', 'relationship'
    ];
    return visualKeywords.some(keyword => prompt.toLowerCase().includes(keyword));
};

/**
 * Process AI response to extract whiteboard content
 */
export const processAIResponseForWhiteboard = (
    response: string,
    userPrompt: string = ''
): {
    chatContent: string;
    whiteboardContent: string | null;
    hasWhiteboard: boolean;
} => {
    let chatContent = response;
    let whiteboardContent: string | null = null;
    let hasWhiteboard = false;

    // 1. Extract JSON Charts
    const jsonChartRegex = /```json:chart([\s\S]*?)```/g;
    const charts: string[] = [];
    chatContent = chatContent.replace(jsonChartRegex, (match, json) => {
        charts.push(match); // Keep the whole block for the whiteboard
        return '[Chart moved to Whiteboard]';
    });

    // 2. Extract Mermaid Diagrams
    const mermaidRegex = /```mermaid([\s\S]*?)```/g;
    const diagrams: string[] = [];
    chatContent = chatContent.replace(mermaidRegex, (match, code) => {
        diagrams.push(match);
        return '[Diagram moved to Whiteboard]';
    });

    // 3. Extract Markdown Tables (if significant)
    // We only move tables if they are explicitly detected or large
    if (containsTables(chatContent)) {
        // This is a simplified extraction. In a robust system, we'd parse the markdown.
        // For now, let's assume if there are tables, we might want to move them if the user asked for visuals
        // Or we can leave them in chat if they are small.
        // The original logic seemed to rely on the AI putting things in specific blocks or just detecting tables.

        // If we want to move tables to whiteboard, we need to extract them.
        // Regex for markdown tables is tricky.
        // Let's stick to the explicit blocks for now, or if the AI was instructed to put them in a specific section.
    }

    // Combine extracted content
    if (charts.length > 0 || diagrams.length > 0) {
        whiteboardContent = [...charts, ...diagrams].join('\n\n');
        hasWhiteboard = true;
    }

    // Also check for explicit "WHITEBOARD:" delimiter if used in prompts
    if (chatContent.includes('WHITEBOARD_CONTENT:')) {
        const parts = chatContent.split('WHITEBOARD_CONTENT:');
        chatContent = parts[0].trim();
        const explicitWhiteboard = parts[1].trim();

        if (whiteboardContent) {
            whiteboardContent += '\n\n' + explicitWhiteboard;
        } else {
            whiteboardContent = explicitWhiteboard;
        }
        hasWhiteboard = true;
    }

    return {
        chatContent,
        whiteboardContent,
        hasWhiteboard
    };
};
